import axios from "axios";
import { localCalc, OFFLINE_CALCS } from "@/lib/calc";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// Custom header required by the backend CSRF guard on state-changing requests
export const CSRF_HEADER = { "X-Requested-With": "rigcalc" };
export const api = axios.create({ baseURL: API, withCredentials: true, headers: CSRF_HEADER });

export function formatApiError(detail) {
  if (detail == null) return "Something went wrong.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export async function runCalc(calculator, inputs, units = "field") {
  // Prefer offline JS engine — instant, works without network
  if (OFFLINE_CALCS.includes(calculator)) {
    return localCalc(calculator, inputs, units);
  }
  const { data } = await api.post("/calc", { calculator, inputs, units });
  return data.result;
}

export async function killSheet(payload) {
  const { data } = await api.post("/kill-sheet", payload);
  return data;
}

export async function listJobs() {
  const { data } = await api.get("/jobs");
  return data;
}

export async function saveJob(job) {
  const { data } = await api.post("/jobs", job);
  return data;
}

export async function deleteJob(id) {
  const { data } = await api.delete(`/jobs/${id}`);
  return data;
}

// Auth
export async function register(email, password, name, referred_by, source) {
  const { data } = await api.post("/auth/register", { email, password, name, referred_by, source });
  return data;
}
export async function login(email, password) {
  const { data } = await api.post("/auth/login", { email, password });
  return data;
}
export async function logout() {
  await api.post("/auth/logout");
}
export async function fetchMe() {
  const { data } = await api.get("/auth/me");
  return data;
}

// Payments
export async function fetchPrices() {
  const { data } = await api.get("/payments/prices");
  return data;
}
export async function fetchLaunchStatus() {
  const { data } = await api.get("/payments/launch-status");
  return data;
}
export async function createCheckout(lookup_key) {
  const { data } = await api.post("/payments/checkout", {
    lookup_key,
    origin_url: window.location.origin,
  });
  return data;
}
export async function fetchStatus(session_id) {
  const { data } = await api.get(`/payments/status/${session_id}`);
  return data;
}

// Teams
export async function fetchMyTeam() {
  const { data } = await api.get("/teams/me");
  return data;
}
export async function joinTeam(join_code) {
  const { data } = await api.post("/teams/join", { join_code });
  return data;
}
export async function leaveTeam() {
  const { data } = await api.post("/teams/leave");
  return data;
}
export async function renameTeam(name) {
  const { data } = await api.patch("/teams/name", { name });
  return data;
}
export async function fetchTeamJobs() {
  const { data } = await api.get("/teams/jobs");
  return data;
}
export async function toggleShareJob(id, shared) {
  const { data } = await api.patch(`/jobs/${id}/share`, { shared });
  return data;
}

// Templates
export async function fetchTemplates(calculator) {
  const params = calculator ? { calculator } : {};
  const { data } = await api.get("/templates", { params });
  return data;
}
export async function promoteToTemplate(id, is_template, rig_name, well_name) {
  const { data } = await api.patch(`/jobs/${id}/promote`, {
    is_template,
    rig_name: rig_name ?? null,
    well_name: well_name ?? null,
  });
  return data;
}

// Referrals
export async function fetchReferral() {
  const { data } = await api.get("/referrals/me");
  return data;
}
export async function applyReferral(code) {
  const { data } = await api.post("/referrals/apply", { code });
  return data;
}

// Specs
export async function fetchSpecs(kind) {
  const { data } = await api.get(`/specs/${kind}`);
  return data;
}

// Team logo
export async function uploadTeamLogo(file) {
  const fd = new FormData();
  fd.append("file", file);
  const { data } = await api.post("/teams/logo", fd, { headers: { "Content-Type": "multipart/form-data" } });
  return data;
}
export async function deleteTeamLogo() {
  const { data } = await api.delete("/teams/logo");
  return data;
}
export function teamLogoUrl() {
  return `${API}/teams/logo`;
}

// PDF
export function jobPdfUrl(id) {
  return `${API}/jobs/${id}/pdf`;
}
export async function downloadJobPdf(id, filename) {
  const res = await fetch(`${API}/jobs/${id}/pdf`, { credentials: "include" });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  const blob = await res.blob();
  _download(blob, filename || `job-${id}.pdf`);
}
export async function downloadKillSheetPdf(name, inputs, outputs) {
  const res = await fetch(`${API}/kill-sheet/pdf`, {
    method: "POST",
    headers: { "Content-Type": "application/json", ...CSRF_HEADER },
    credentials: "include",
    body: JSON.stringify({ name, inputs, outputs }),
  });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  const blob = await res.blob();
  _download(blob, `${name.replace(/\s+/g, "_")}.pdf`);
}
function _download(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

// AI stream (protected)
export function streamAI(sessionId, message, context, onChunk, onDone, onError) {
  const controller = new AbortController();
  fetch(`${API}/ai/chat`, {
    method: "POST",
    headers: { "Content-Type": "application/json", ...CSRF_HEADER },
    credentials: "include",
    body: JSON.stringify({ session_id: sessionId, message, context }),
    signal: controller.signal,
  })
    .then(async (res) => {
      if (res.status === 401) {
        onError?.(new Error("Please sign in to use the AI assistant."));
        return;
      }
      if (res.status === 402) {
        onError?.(new Error("PAYWALL"));
        return;
      }
      if (!res.ok || !res.body) {
        onError?.(new Error(`HTTP ${res.status}`));
        return;
      }
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buf = "";
      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buf += decoder.decode(value, { stream: true });
        const parts = buf.split("\n\n");
        buf = parts.pop() || "";
        for (const part of parts) {
          const line = part;
          if (!line.startsWith("data:")) continue;
          const payload = line.slice(5).replace(/^ /, "");
          if (payload === "[DONE]") {
            onDone?.();
            return;
          }
          if (payload.startsWith("[ERROR]")) {
            onError?.(new Error(payload));
            return;
          }
          try {
            const decoded = atob(payload);
            const text = decodeURIComponent(escape(decoded));
            onChunk?.(text);
          } catch {
            onChunk?.(payload);
          }
        }
      }
      onDone?.();
    })
    .catch(onError);
  return () => controller.abort();
}

// Shareable results
export async function createShare(payload) {
  const { data } = await api.post("/shares", payload);
  return data;
}
export async function getShare(id) {
  const { data } = await api.get(`/shares/${id}`);
  return data;
}

// Emergent-managed Google sign-in: exchange one-time session_id for app cookies
export async function googleSession(session_id, source, referred_by) {
  const { data } = await api.post("/auth/google", { session_id, source, referred_by });
  return data;
}

// Promo codes (ad attribution + discount)
export async function lookupPromo(code) {
  const { data } = await api.get(`/promos/${encodeURIComponent(code)}`);
  return data;
}
export async function claimPromo(code) {
  const { data } = await api.post("/promos/claim", { code });
  return data;
}
export async function getMyPromo() {
  const { data } = await api.get("/promos/mine");
  return data.promo;
}

export async function downloadTallyPdf(name, inputs, outputs) {
  const res = await fetch(`${API}/tally/pdf`, {
    method: "POST",
    headers: { "Content-Type": "application/json", ...CSRF_HEADER },
    credentials: "include",
    body: JSON.stringify({ name, inputs, outputs }),
  });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  _download(await res.blob(), `${name.replace(/[^A-Za-z0-9._-]+/g, "_")}.pdf`);
}

// Job photos
export const jobPhotoUrl = (jobId, photoId) => `${API}/jobs/${jobId}/photos/${photoId}`;
export const sharePhotoUrl = (shareId, photoId) => `${API}/shares/${shareId}/photos/${photoId}`;
export async function uploadJobPhoto(jobId, file) {
  const form = new FormData();
  form.append("file", file);
  const { data } = await api.post(`/jobs/${jobId}/photos`, form);
  return data;
}
export async function deleteJobPhoto(jobId, photoId) {
  await api.delete(`/jobs/${jobId}/photos/${photoId}`);
}

// Formula guides (locked content)
export async function fetchGuide(slug) {
  const { data } = await api.get(`/guides/${slug}`);
  return data;
}
